import React from "react";
import { Link } from "react-router-dom";
import { PhoneIcon, EnvelopeIcon } from "@heroicons/react/24/outline";

function PrivacyPolicy() {
  const sections = [
    {
      title: "Introduction",
      content: (
        <p className="text-gray-200 leading-relaxed">
          Ingersoll Built ("we", "us", "our", or "Company") operates the
          ingersollbuilt.ca website (the "Service"). This page informs you of
          our policies regarding the collection, use, and disclosure of personal
          data when you use our Service and the choices you have associated with
          that data.
        </p>
      ),
    },
    {
      title: "Information Collection and Use",
      content: (
        <>
          <p className="text-gray-200 mb-3 leading-relaxed">
            We collect several different types of information for various
            purposes to provide and improve our Service to you.
          </p>
          <div className="ml-4 space-y-3">
            <div>
              <h3 className="font-semibold text-white mb-2">Personal Data:</h3>
              <p className="text-gray-200 leading-relaxed">
                While using our Service, we may ask you to provide us with
                certain personally identifiable information that can be used to
                contact or identify you ("Personal Data"). This may include, but
                is not limited to:
              </p>
              <ul className="list-disc ml-6 mt-2 text-gray-200 space-y-1">
                <li>Email address</li>
                <li>First name and last name</li>
                <li>Phone number</li>
                <li>Cookies and Usage Data</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">Usage Data:</h3>
              <p className="text-gray-200 leading-relaxed">
                We may also collect information about how the Service is
                accessed and used ("Usage Data"). This may include information
                such as your computer's Internet Protocol address (e.g. IP
                address), browser type, browser version, the pages you visit,
                the time and date of your visit, and other diagnostic data.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Use of Data",
      content: (
        <>
          <p className="text-gray-200 mb-3 leading-relaxed">
            Ingersoll Built uses the collected data for various purposes:
          </p>
          <ul className="list-disc ml-6 text-gray-200 space-y-2">
            <li>To provide and maintain our Service</li>
            <li>To notify you about changes to our Service</li>
            <li>
              To allow you to participate in interactive features of our Service
            </li>
            <li>To provide customer care and support</li>
            <li>
              To gather analysis or valuable information so we can improve our
              Service
            </li>
            <li>To monitor the usage of our Service</li>
            <li>To detect, prevent and address technical issues</li>
          </ul>
        </>
      ),
    },
    {
      title: "Security of Data",
      content: (
        <p className="text-gray-200 leading-relaxed">
          The security of your data is important to us, but remember that no
          method of transmission over the Internet or method of electronic
          storage is 100% secure. While we strive to use commercially acceptable
          means to protect your Personal Data, we cannot guarantee its absolute
          security.
        </p>
      ),
    },
    {
      title: "Contact Us",
      content: (
        <>
          <p className="text-gray-200 mb-4 leading-relaxed">
            If you have any questions about this Privacy Policy, please contact
            us:
          </p>
          <div className="bg-gray-700 bg-opacity-50 p-4 rounded space-y-2 border border-white border-opacity-10">
            <p className="text-gray-200 flex items-center gap-2">
              <PhoneIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              (555) 123-4567
            </p>
            <p className="text-gray-200 flex items-center gap-2">
              <EnvelopeIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              info@ingersollbuilt.ca
            </p>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <Link
            to="/"
            className="text-cyan-400 hover:text-white transition mb-4 inline-block"
          >
            ← Back to Home
          </Link>
          <h1 className="text-5xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-gray-300">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content */}
        <div className="space-y-5 animate-slide-in-right">
          {sections.map((section, index) => (
            <section
              key={section.title}
              className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-8 hover-card animate-fade-in"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <h2 className="text-2xl font-bold mb-4 text-cyan-400">
                {section.title}
              </h2>
              {section.content}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
